/**
 * Main App Component
 *
 * Sets up routing, authentication context, and main layout structure.
 */

import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, Typography, Container, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { lazy, Suspense, type ReactNode } from 'react';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './hooks/useAuth';
import AppBar from './components/Layout/AppBar';
import LoadingSpinner from './components/Common/LoadingSpinner';

const CriteriaPage = lazy(() => import('./pages/CriteriaPage'));
const AdsPage = lazy(() => import('./pages/AdsPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const MortgagePage = lazy(() => import('./pages/MortgagePage'));
const ScrapingDashboardPage = lazy(() => import('./pages/ScrapingDashboardPage'));

interface RouteGuardProps {
  children: ReactNode;
}

/**
 * Protected Route Component
 * Redirects to login if user is not authenticated
 */
const ProtectedRoute = ({ children }: RouteGuardProps): ReactNode => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

/**
 * Admin Route Component
 * Shows 403 error if user is not admin
 */
const AdminRoute = ({ children }: RouteGuardProps): ReactNode => {
  const { t } = useTranslation(['common']);
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isAdmin) {
    return (
      <Container maxWidth="sm">
        <Paper sx={{ p: 4, mt: 8, textAlign: 'center' }}>
          <Typography variant="h4" color="error" gutterBottom>
            {t('errors.forbiddenTitle')}
          </Typography>
          <Typography variant="body1">{t('errors.forbiddenMessage')}</Typography>
        </Paper>
      </Container>
    );
  }

  return children;
};

/**
 * Main App Component
 */
function App(): ReactNode {
  const { t } = useTranslation(['common']);

  return (
    <AuthProvider>
      <Router>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          {/* Navigation bar */}
          <AppBar />

          {/* Main content area */}
          <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />

                {/* Protected routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Navigate to="/ads" replace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Navigate to="/ads" replace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/criteria"
                  element={
                    <ProtectedRoute>
                      <CriteriaPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ads/:id"
                  element={
                    <ProtectedRoute>
                      <AdsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ads"
                  element={
                    <ProtectedRoute>
                      <AdsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/workflow"
                  element={
                    <ProtectedRoute>
                      <Navigate to="/ads" replace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/mortgage"
                  element={
                    <ProtectedRoute>
                      <MortgagePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/scraping"
                  element={
                    <ProtectedRoute>
                      <ScrapingDashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/scraping/runs/:id"
                  element={
                    <ProtectedRoute>
                      <ScrapingDashboardPage />
                    </ProtectedRoute>
                  }
                />

                {/* Admin routes */}
                <Route
                  path="/admin"
                  element={
                    <AdminRoute>
                      <AdminPage />
                    </AdminRoute>
                  }
                />

                {/* 404 - Not found */}
                <Route path="*" element={<div>{t('errors.notFound')}</div>} />
              </Routes>
            </Suspense>
          </Box>

          {/* Footer (optional) */}
          <Box
            component="footer"
            sx={{
              py: 2,
              px: 3,
              mt: 'auto',
              backgroundColor: (theme) => theme.palette.grey[100],
              textAlign: 'center',
            }}
          >
            {t('footer')}
          </Box>
        </Box>
      </Router>
    </AuthProvider>
  );
}

export default App;
